import { useEffect, useState } from "react";

export default function useTableHeight(
  containerRef: React.RefObject<HTMLDivElement>,
  showHeader: boolean,
  hasFilters: boolean = false,
  // Paginated tables pass no scroll.y, so the measured height is discarded. Skip the
  // observer and the layout reads entirely rather than doing them for nothing.
  enabled: boolean = true,
) {
  const [tableHeight, setTableHeight] = useState<number>(400); // Default fallback height

  // ResizeObserver to dynamically calculate table height
  useEffect(() => {
    if (!enabled) return;

    const calculateTableHeight = () => {
      if (!containerRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      let nonTableHeight = 0;

      // Find all non-table elements in the container and sum their heights
      const allChildren = Array.from(containerRef.current.children);

      allChildren.forEach((child) => {
        const element = child as HTMLElement;

        // Skip the actual table wrapper element
        if (!element.classList.contains("ant-table-wrapper")) {
          nonTableHeight += element.getBoundingClientRect().height;
        }
      });

      // Also account for table header if present and shown
      const tableElement =
        containerRef.current.querySelector(".ant-table-thead");
      if (tableElement && showHeader) {
        nonTableHeight += tableElement.getBoundingClientRect().height;
      }

      // Calculate available height for table body
      const availableHeight = containerHeight - nonTableHeight;

      // Ensure minimum height and that we have a valid height
      const finalHeight = Math.max(availableHeight, 200);

      // Bail out on sub-pixel changes. availableHeight comes from
      // getBoundingClientRect(), so it is fractional, and this value feeds scroll.y -
      // the height of the very element the ResizeObserver watches. Setting state for a
      // fraction of a pixel re-arms the observer through requestAnimationFrame and can
      // spin every frame. useScrollStatus guards itself the same way.
      setTableHeight((prev) =>
        Math.abs(prev - finalHeight) < 1 ? prev : finalHeight,
      );
    };

    // Initial calculation with delay to ensure DOM is ready
    const initialTimer = setTimeout(calculateTableHeight, 100);

    // ResizeObserver for dynamic updates
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(calculateTableHeight);
    });

    // Window resize listener as fallback for environments where ResizeObserver might not work properly
    const handleWindowResize = () => {
      requestAnimationFrame(calculateTableHeight);
    };

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      window.addEventListener("resize", handleWindowResize);
    }

    return () => {
      clearTimeout(initialTimer);
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [showHeader, containerRef, hasFilters, enabled]);

  return tableHeight;
}
